import React from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Button, SelectOptionElement } from '@odin-labs/components';
import { CardStatus, WorkerCardType } from 'apollo/generated/client-operations';
import { cn, getSingleOrNull } from 'utils';
import { IdCardIcon } from 'components/icons';
import { FormInputTypes, GridColSpan, TypedFormInputs } from 'components/form';
import { BadgeChecklist } from 'components/badgeChecklist/BadgeChecklist';
import { TempBadgeOptionElement } from 'containers/worker/helpers/useTempBadgeOptions';
import { WorkerCard } from 'containers/worker/modals/badgeModal/components/types';
import { AssignTempBadgeFormData } from './types';

type GetFormInputsHookArgs = {
  jobsiteId: string;
  proximityCardFormatOptions: SelectOptionElement[];
  tempBadgeOptions: TempBadgeOptionElement[];
};

export const getFormInputs = (
  args: GetFormInputsHookArgs,
): TypedFormInputs<AssignTempBadgeFormData> => {
  const { jobsiteId, proximityCardFormatOptions, tempBadgeOptions } = args;

  return {
    tempBadgeId: {
      element: FormInputTypes.Select,
      label: 'Temporary badge',
      elementProps: {
        icon: IdCardIcon,
        options: tempBadgeOptions,
      },
      validation: { required: true },
      layout: GridColSpan.SpanFull,
    },
    tempBadgesLink: {
      element: FormInputTypes.CustomContent,
      elementProps: {
        content: (
          <Button
            as={Link}
            theme="naked"
            to={`/jobsite/${jobsiteId}/temp-badges`}
            text="Print more temporary badges"
            className="odin-underline"
          />
        ),
      },
      layout: cn(GridColSpan.SpanFull, '-odin-mt-3'),
    },
    expirationDate: {
      element: FormInputTypes.DatePicker,
      label: 'Expiration date',
      elementProps: {
        minDate: moment(),
        showDefaultIcon: true,
        numberOfMonths: 1,
      },
      validation: { required: true },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    },
    tempBadgeReason: {
      element: FormInputTypes.Field,
      label: 'Reason',
      validation: { required: true },
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    },
    proximityCardFormatId: {
      element: FormInputTypes.Select,
      label: 'Card format',
      elementProps: {
        disabled: true,
        options: proximityCardFormatOptions ?? [],
      },
      validation: { required: false },
      layout: 'odin-hidden',
    },
    badges: {
      element: FormInputTypes.CustomInput,
      elementProps: {
        customInput: BadgeChecklist,
      },
      layout: GridColSpan.SpanFull,
    },
  };
};

export const getFormDefaultValues = (args: {
  workerCards: WorkerCard[];
  proximityCardFormatOptions: SelectOptionElement[];
}): AssignTempBadgeFormData => {
  const { workerCards, proximityCardFormatOptions } = args;

  return {
    proximityCardFormatId: getSingleOrNull(proximityCardFormatOptions),
    tempBadgeId: null,
    expirationDate: moment().add({ day: 1 }),
    tempBadgeReason: '',
    badges:
      workerCards
        ?.filter(
          ({ cardStatus, workerCardFormat }) =>
            cardStatus === CardStatus.Active &&
            workerCardFormat.cardType === WorkerCardType.Proximity,
        )
        ?.map((workerCard) => ({ workerCard, selected: true })) ?? null,
  };
};
